import { Link } from "react-router-dom";
import { useState } from "react";
import { Play } from "lucide-react";
import { embedKind } from "@/lib/embeds";
import MediaRenderer, { derivePreset } from "./MediaRenderer";
import { sectionScheme, schemeAccentHex, schemeHex } from "@/lib/schemes";

/**
 * MediaTile — the flexible tile used across the homepage showcase, artist highlights,
 * scene features, product previews and after-dark items.
 *
 * Props (all optional except title):
 *   title              — main caption (kept short)
 *   caption            — muted second line (creator / discipline / format)
 *   image_url          — cover / thumbnail
 *   image_url_alt      — hover-swap OR the "real" media revealed on hover
 *   embed_url          — if set + kind is audio/video/reel → shows a tiny play control
 *   render_preset      — "original" | "pixel" | "dither" | "ascii" | "crt" | "rgb-glitch" | "monochrome" | "loop"
 *   render_asset_url   — for the "loop" preset (mp4/webm/gif)
 *   auto_preset        — bool. If true and preset is falsy, derive one from the seed so
 *                        the homepage naturally becomes a gallery of interpretations.
 *   seed               — pixel-placeholder seed
 *   href               — where the caption / whole tile navigates
 *   onOpen             — opens a QuickView (used only for shop items now)
 *   aspect             — "square" | "tall" | "wide"
 *   dark
 *   playHref, playSubtitle — routed to the sticky player when the play control fires
 *   testId
 */
export default function MediaTile({
    title,
    caption,
    image_url,
    image_url_alt,
    embed_url,
    render_preset,
    render_asset_url,
    auto_preset = false,
    seed,
    href,
    onOpen,
    aspect = "square",
    dark = false,
    scheme,           // optional: applies a scheme_bg / scheme_fg to the caption block
    testId,
    playHref,
    playSubtitle,
    showPlay = true,
    playBelow = false,   // legacy — play now lives inside the image again
    clickAction = "",    // "" (href default) | "play" — what clicking the tile does
}) {
    const [hover, setHover] = useState(false);
    // NOTE: per-item scheme (admin picker) colors THIS tile's effect; when unset
    // the effect inherits the section/page palette. Global switch OFF = original colors.
    const eKind = embedKind(embed_url || "");
    const hasPlayableEmbed = !!embed_url && (eKind === "audio" || eKind === "video" || eKind === "vertical-video");
    const playable = showPlay && hasPlayableEmbed;
    const actionIsPlay = clickAction === "play" && !!embed_url;

    const playMedia = () => {
        window.dispatchEvent(new CustomEvent("itzkatu:play", {
            detail: {
                url: embed_url,
                kind: "auto",
                title,
                subtitle: playSubtitle || caption || "",
                href: playHref || "",
            },
        }));
    };
    const aspectClass = aspect === "tall" ? "aspect-[3/4]" : aspect === "wide" ? "aspect-[16/9]" : "aspect-square";

    // Preset resolution — explicit takes priority, then auto-derive if enabled.
    const preset = (render_preset && render_preset !== "")
        ? render_preset
        : (auto_preset ? derivePreset(seed || title || "") : "original");

    // Optional per-item scheme — applies to the caption AND the media background,
    // so the tile can visually break away from the page palette (e.g. a shop item
    // rendered on a cream card even when the page is charcoal).
    const schemeStyle = (scheme && scheme !== "") ? sectionScheme(scheme) : { bg: "", fg: "" };

    // Map scheme name → hex accent for the overlay/tint. Kept in sync with
    // /lib/schemes.js so page, tile and media effects always agree.
    const schemeAccent = scheme ? schemeAccentHex(scheme) : null;
    // When the tile has its own explicit scheme, override the CSS vars
    // for its subtree so the canvas effect uses the tile's palette instead
    // of the section/page's. Without a tile scheme, the effect inherits
    // whatever palette the section/page provides (which is the whole point).
    const schemeVarStyle = scheme ? (() => {
        const hex = schemeHex(scheme);
        return {
            "--sc-bg": hex.bg,
            "--sc-text": hex.text,
            "--sc-subtext": hex.subtext,
            "--sc-accent": hex.accent,
            "--sc-accent2": hex.accent2,
            "--sc-border": hex.border,
            "--sc-card": hex.card,
        };
    })() : {};

    // On hover, if the render preset is experimental AND we have the original image,
    // fade it in over the interpretation — "discover the real thing on hover".
    const hasInterpretation = preset !== "original" && preset !== "monochrome" && preset !== "";
    // If there IS an explicit alt image → fade to it. If there is NOT and a scheme
    // is set → fade to a scheme-tinted version of the original. If neither, no reveal.
    const revealOriginal = hasInterpretation && !!image_url;

    const openModal = (e) => {
        if (onOpen) {
            e.preventDefault();
            onOpen();
        }
    };

    const media = (
        <div
            className={`relative w-full ${aspectClass} overflow-hidden ${schemeStyle.bg || (dark ? "bg-[#0B0B0C]" : "bg-[#F5F5F5]")}`}
            data-testid={testId ? `${testId}-media` : undefined}
        >
            {/* Base layer — the interpretation (or original if preset === "original") */}
            <div className={`absolute inset-0 transition-opacity duration-500 ${revealOriginal && hover ? "opacity-0" : "opacity-100"}`}>
                <MediaRenderer
                    src={image_url}
                    alt={title}
                    preset={preset}
                    asset_url={render_asset_url}
                    seed={seed || title}
                    dark={dark}
                    scheme_accent={schemeAccent || ""}
                />
            </div>

            {/* Reveal layer — either the explicit alt image or a scheme-tinted version
                of the original. This is what makes the scheme "inherit into the hover":
                if no alt image is provided, the reveal keeps the same visual language
                (accent color from the scheme) instead of jumping to raw photo colors. */}
            {revealOriginal && (
                <div className={`absolute inset-0 transition-opacity duration-500 ${hover ? "opacity-100" : "opacity-0"}`}>
                    <img
                        src={image_url_alt || image_url}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-cover"
                    />
                </div>
            )}

            {/* Legacy image_url_alt behavior when preset is "original" — plain swap on hover */}
            {!hasInterpretation && image_url_alt && image_url_alt !== image_url && (
                <img
                    src={image_url_alt}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${hover ? "opacity-100" : "opacity-0"}`}
                />
            )}

            {/* Play control — only when a playable embed exists. Small, corner. */}
            {playable && !playBelow && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        playMedia();
                    }}
                    aria-label={`play ${title}`}
                    data-testid={testId ? `${testId}-play` : undefined}
                    className="absolute left-3 bottom-3 bg-black/85 text-white rounded-full w-10 h-10 border-2 border-white flex items-center justify-center transition-transform hover:scale-110"
                >
                    <Play size={14} fill="currentColor" style={{ marginLeft: 1 }} />
                </button>
            )}

            {/* Subtle open-indicator — bigger, white-bordered circle so it reads as an
                intentional "open" affordance (Ksawery-style). Only on hover, never
                when a playable embed is showing its own control. */}
        </div>
    );

    const caps = (
        <div className={`mt-3 md:mt-4 text-left ${schemeStyle.fg}`}>
            {title && (
                <div className={`font-heading font-black text-lg md:text-xl uppercase tracking-tight leading-[1.1] ${dark && !schemeStyle.fg ? "text-white" : "text-current"}`}>
                    {title}
                </div>
            )}
            {caption && (
                <div className={`font-body italic capitalize text-lg md:text-xl leading-[1.15] ${dark && !schemeStyle.fg ? "text-white/70" : "opacity-70"}`}>
                    {caption}
                </div>
            )}
        </div>
    );

    const wrapperClass = "group flex flex-col cursor-pointer";
    const setHoverOn = () => setHover(true);
    const setHoverOff = () => setHover(false);

    // Click action override — "play" turns the whole tile into a play trigger
    // (chosen per item in the admin scene editor).
    if (actionIsPlay) {
        return (
            <div
                onClick={playMedia}
                onMouseEnter={setHoverOn}
                onMouseLeave={setHoverOff}
                className={wrapperClass}
                style={schemeVarStyle}
                data-testid={testId}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        playMedia();
                    }
                }}
            >
                {media}
                {caps}
            </div>
        );
    }

    if (href && !onOpen) {
        const internal = href.startsWith("/");
        return internal ? (
            <Link
                to={href}
                className={wrapperClass}
                style={schemeVarStyle}
                onMouseEnter={setHoverOn}
                onMouseLeave={setHoverOff}
                data-testid={testId}
            >
                {media}
                {caps}
            </Link>
        ) : (
            <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className={wrapperClass}
                style={schemeVarStyle}
                onMouseEnter={setHoverOn}
                onMouseLeave={setHoverOff}
                data-testid={testId}
            >
                {media}
                {caps}
            </a>
        );
    }

    return (
        <div
            onClick={openModal}
            onMouseEnter={setHoverOn}
            onMouseLeave={setHoverOff}
            className={wrapperClass}
            style={schemeVarStyle}
            data-testid={testId}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && onOpen) {
                    e.preventDefault();
                    onOpen();
                }
            }}
        >
            {media}
            {caps}
        </div>
    );
}
